'use client';

import { useState, useCallback } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import Button from '@mui/material/Button';
import EventList from '@/components/event-list';
import SocialLinks from '@/components/social-links';
import Markdown from '@/components/markdown';
import UserHeader from '@/components/users/header';
import EventsGrid from '@/components/users/events-grid';
import EventSeriesSection from '@/components/groups/event-series-section';
import Main from '@/components/main';
import { userMoreEventsQuery, userPastEventsQuery } from '@/apollo/users';
import type { User } from '@/types/User';
import type { Event } from '@/types/Event';

const PAGE_SIZE = 10;

type Props = { initialUser: User };

const PersonPageClient = ({ initialUser }: Props) => {
  const user = initialUser;

  const [view, setView] = useState<'upcoming' | 'past'>('upcoming');

  // Upcoming load-more state
  const [moreUpcoming, setMoreUpcoming] = useState<Event[]>([]);
  const [upcomingOffset, setUpcomingOffset] = useState(PAGE_SIZE);
  const [loadMoreUpcoming, { loading: loadingMore }] = useLazyQuery(userMoreEventsQuery);

  const allUpcoming = [...(user.hostedEvents ?? []), ...moreUpcoming];
  const hasMoreUpcoming = allUpcoming.length < (user.hostedEventsCount ?? 0);

  const handleLoadMoreUpcoming = useCallback(async () => {
    const { data } = await loadMoreUpcoming({ variables: { username: user.username, offset: upcomingOffset } });
    const newEvents = data?.user?.hostedEvents ?? [];
    setMoreUpcoming((prev) => [...prev, ...newEvents]);
    setUpcomingOffset((prev) => prev + newEvents.length);
  }, [loadMoreUpcoming, user.username, upcomingOffset]);

  // Past events lazy load
  const [pastEvents, setPastEvents] = useState<Event[]>([]);
  const [pastOffset, setPastOffset] = useState(0);
  const [pastLoaded, setPastLoaded] = useState(false);
  const [loadPastEvents, { loading: loadingPast }] = useLazyQuery(userPastEventsQuery);

  const hasMorePast = pastEvents.length < (user.pastHostedEventsCount ?? 0);

  const handleViewChange = useCallback(async (_e: React.SyntheticEvent, newView: 'upcoming' | 'past' | null) => {
    if (!newView) return;
    setView(newView);
    if (newView === 'past' && !pastLoaded) {
      const { data } = await loadPastEvents({ variables: { username: user.username, offset: 0 } });
      const newEvents = data?.user?.pastHostedEvents ?? [];
      setPastEvents(newEvents);
      setPastOffset(newEvents.length);
      setPastLoaded(true);
    }
  }, [pastLoaded, loadPastEvents, user.username]);

  const handleLoadMorePast = useCallback(async () => {
    const { data } = await loadPastEvents({ variables: { username: user.username, offset: pastOffset } });
    const newEvents = data?.user?.pastHostedEvents ?? [];
    setPastEvents((prev) => [...prev, ...newEvents]);
    setPastOffset((prev) => prev + newEvents.length);
  }, [loadPastEvents, user.username, pastOffset]);

  return (
    <Main compact>
      <UserHeader user={user} />

      <SocialLinks source={user} />

      <Markdown>{user.about}</Markdown>

      <EventsGrid
        view={view}
        hostedEventsCount={user.hostedEventsCount ?? 0}
        pastHostedEventsCount={user.pastHostedEventsCount ?? 0}
        onChange={handleViewChange}
      >
        {view === 'upcoming' ? (
          <>
            <EventList
              events={allUpcoming}
              emptyMessage="Not hosting any upcoming public events"
              fullDate
            />
            {hasMoreUpcoming && (
              <Button
                variant="outlined"
                fullWidth
                onClick={handleLoadMoreUpcoming}
                disabled={loadingMore}
                sx={{ mt: 2 }}
              >
                {loadingMore ? 'Loading...' : 'Load more'}
              </Button>
            )}
          </>
        ) : (
          <>
            <EventList
              events={pastEvents}
              emptyMessage={pastLoaded ? 'No past public events' : 'Loading...'}
              fullDate
            />
            {hasMorePast && pastLoaded && (
              <Button
                variant="outlined"
                fullWidth
                onClick={handleLoadMorePast}
                disabled={loadingPast}
                sx={{ mt: 2 }}
              >
                {loadingPast ? 'Loading...' : 'Load more'}
              </Button>
            )}
          </>
        )}
      </EventsGrid>

      {user.hostedEventSeries && (
        <EventSeriesSection
          eventSeries={user.hostedEventSeries}
          editable={false}
        />
      )}
    </Main>
  );
};

export default PersonPageClient;
